import React, { useEffect, useRef } from 'react';
import TrackingAPI from 'api/TrackingAPI';
import CloudIcon from 'assets/cloud_outlined_icon.svg?react';
import void_ava from 'assets/gap_avatar.svg';
import InfoChip from 'assets/info_chip.svg?react';
import CommonHelper from 'helpers/CommonHelper';
import styled from 'styled-components';

interface Character {
  id: string;
  name: string;
  isUser: boolean;
  avatarsMap: {
    default: string;
  };
}

interface MobileNavigationPanelProps {
  characters: Character[];
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  scenarioId: string;
  game: Record<string, unknown>;
}

const MobileNavigationPanel: React.FC<MobileNavigationPanelProps> = ({
  characters = [],
  activeTab = 'chat',
  onTabChange = () => { },
  scenarioId,
  game,
}) => {
  const scrollableTabsRef = useRef<HTMLDivElement>(null);
  const activeTabRef = useRef<HTMLDivElement>(null);




  const handleTabChange = (tab: string) => {
    onTabChange(tab);

    const analyticsData = {
      scenarioId,
      config: game,
      characterName: tab,
    };

    if (tab !== 'chat' && tab !== 'about') {
      TrackingAPI.trackEvent('scenario_navigation_panel_character_click', analyticsData);
    }
  };

  const filteredCharacters = characters.filter((character) => !character?.isUser);
  useEffect(() => {
    if (scrollableTabsRef.current && activeTabRef.current && activeTab !== 'chat') {
      const container = scrollableTabsRef.current;
      const activeElement = activeTabRef.current;

      const containerWidth = container.offsetWidth;
      const activeElementLeft = activeElement.offsetLeft;
      const activeElementWidth = activeElement.offsetWidth;

      const scrollLeft = activeElementLeft - (containerWidth / 2) + (activeElementWidth / 2);

      container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  }, [activeTab]);

  return (
    <NavigationWrapper>
      <FixedTabs>
        <TabItem
          active={activeTab === 'chat'}
          onClick={() => handleTabChange('chat')}
        >
          <IconContainer>
            <StyledCloudIcon active={activeTab === 'chat'}>
              <CloudIcon />
            </StyledCloudIcon>
          </IconContainer>
          <TabText>Chat</TabText>
        </TabItem>
      </FixedTabs>

      <ScrollableTabs ref={scrollableTabsRef}>
        <TabItem
          active={activeTab === 'about'}
          onClick={() => handleTabChange('about')}
          ref={activeTab === 'about' ? activeTabRef : undefined}
        >
          <IconContainer>
            <StyledInfoChip active={activeTab === 'about'}>
              <InfoChip height={22} width={22} />
            </StyledInfoChip>
          </IconContainer>
          <TabText>About</TabText>
        </TabItem>
        {filteredCharacters?.map((character) => (
          <TabItem
            key={character.id || character.name}
            active={activeTab === character.name}
            onClick={() => handleTabChange(character.name)}
            ref={activeTab === character.name ? activeTabRef : undefined}
          >
            <IconContainer>
              <CharacterAvatar
                src={character.avatarsMap?.default ? CommonHelper.getFilesCdnUrl(character.avatarsMap.default) : void_ava}
                alt={character.name}
              />
            </IconContainer>
            <TabText>{character.name}</TabText>
          </TabItem>
        ))}
      </ScrollableTabs>
    </NavigationWrapper>
  );
};

export default MobileNavigationPanel;

const NavigationWrapper = styled.div`
  display: flex;
  align-items: center;
  background: #131313;
  height: 70px;
  width: 100%;
`;

const FixedTabs = styled.div`
  display: flex;
  align-items: center;
  border-right: 1px solid rgba(255, 255, 255, 0.1);
`;

const ScrollableTabs = styled.div`
  display: flex;
  align-items: center;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const TabItem = styled.div<{ active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 70px;
  height: 68px;
  cursor: pointer;
  color: ${props => props.active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.5)'};
  background-color: ${props => props.active ? '#1d1d1d' : '#131313'};
  transition: color 0.2s ease, background-color 0.2s ease;

  &:hover {
    color: #FFFFFF;
  }
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
`;

const TabText = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  line-height: 16px;
  margin-top: 4px;
`;


const CharacterAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
`;

const StyledCloudIcon = styled.div<{ active?: boolean }>`
  svg {
    path {
      stroke: ${props => props.active ? 'white' : '#A9A9A9'} !important;
      fill: none !important;
    }
  }
  display: flex;
`;

const StyledInfoChip = styled.div<{ active?: boolean }>`
  svg {
    path {
      fill: ${props => props.active ? 'white' : '#A9A9A9'} !important;
    }
    * {
      fill: ${props => props.active ? 'white' : '#A9A9A9'} !important;
    }
  }
  display: flex;
`;
